export type LocationType = "region" | "city" | "neighborhood" | "destination" | "parkingLot" | "unknown";

export interface LocationUrlData {
  type: LocationType;
  regionCode: string | null;
  citySlug: string | null;
  neighborhoodSlug: string | null;
  destinationSlug: string | null;
  parkingLotSlug: string | null;
  segments: string[];
}

export function parseLocationUrl(fullSlug: string): LocationUrlData {
  const segments = fullSlug
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);

  const locationsIndex = segments.findIndex((seg) => seg === "locations");

  if (locationsIndex === -1) {
    return {
      type: "unknown",
      regionCode: null,
      citySlug: null,
      neighborhoodSlug: null,
      destinationSlug: null,
      parkingLotSlug: null,
      segments: [],
    };
  }

  const locationSegments = segments.slice(locationsIndex + 1);

  const regionCode = locationSegments[0] || null;
  const citySlug = locationSegments[1] || null;

  let neighborhoodSlug: string | null = null;
  let destinationSlug: string | null = null;
  let parkingLotSlug: string | null = null;
  let type: LocationType = "unknown";

  if (locationSegments.length === 1 && regionCode) {
    type = "region";
  } else if (locationSegments.length === 2 && citySlug) {
    type = "city";
  } else if (locationSegments.length >= 4) {
    const subType = locationSegments[2];
    const subSlug = locationSegments[3];

    if (subType === "neighbourhood" && subSlug) {
      type = "neighborhood";
      neighborhoodSlug = subSlug;
    } else if (subType === "destination" && subSlug) {
      type = "destination";
      destinationSlug = subSlug;
    } else if (subType === "parking-lot" && subSlug) {
      type = "parkingLot";
      parkingLotSlug = subSlug;
    }
  }

  return {
    type,
    regionCode,
    citySlug,
    neighborhoodSlug,
    destinationSlug,
    parkingLotSlug,
    segments: locationSegments,
  };
}

export function slugToName(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getStateCode(regionCode: string | null): string | null {
  if (!regionCode) return null;
  return regionCode.toUpperCase();
}

export interface ParsedAddress {
  streetAddress: string;
  addressLocality: string;
  addressRegion: string;
  postalCode: string;
  addressCountry: string;
}

export function parseAddress(addressString: string): ParsedAddress | null {
  const parts = addressString.split(",").map((p) => p.trim());

  if (parts.length < 3) return null;

  const streetAddress = parts[0] ?? "";
  const addressLocality = parts[1] ?? "";
  const stateZipPart = parts[2] ?? "";

  const stateZipMatch = stateZipPart.match(/^([A-Z]{2})\s*,?\s*(\d{5})?$/);

  if (!stateZipMatch || !stateZipMatch[1]) {
    const simpleParts = stateZipPart.split(/\s+/);
    return {
      streetAddress,
      addressLocality,
      addressRegion: simpleParts[0] ?? "",
      postalCode: parts[3] ?? simpleParts[1] ?? "",
      addressCountry: "United States",
    };
  }

  return {
    streetAddress,
    addressLocality,
    addressRegion: stateZipMatch[1],
    postalCode: stateZipMatch[2] ?? parts[3] ?? "",
    addressCountry: "United States",
  };
}
